/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo, useRef } from "react";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import { Grid, Icon, Popover } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftPagination from "components/SoftPagination";
import DataTable from "../Tables/DataTable";

// 🔹 Filters – updated version
import UserFilters from "layouts/interviewSelected/components/useFilters";

// 🔹 Calendar
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

// 🔹 Drawer
import OvertimeDrawer from "./OvertimeDrawer";

// 🔹 Zustand
import useOvertimeStore from "store/useOvertimeStore";

// 🔐 Auth
import { useAuth } from "AuthContext";
import { hasPermission } from "permissionUtils";

// ✅ NEW hook – lazy-loaded dropdowns
import { useDropdownOptions } from "layouts/interviewSelected/hooks/useDropdownOptions";

// Helper for leave status
import { getLeaveStatusInfo } from "layouts/leavetracker/helper";
import FlagLoader from "layouts/flagloader";

/* ================= DEBOUNCE ================= */
const useDebounce = (value, delay = 500) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

/* ================= CLEAN PARAMS ================= */
const cleanParams = (obj) => {
  const p = {};
  Object.keys(obj).forEach((k) => {
    const v = obj[k];
    if (v !== "" && v !== "all" && v !== null && v !== undefined) {
      p[k] = v;
    }
  });
  return p;
};

function MyOvertimeRequest() {
  const { userPermission = [] } = useAuth();
  const canViewPage = true;
  const canCreateOvertime = true;

  /* ================= STORE ================= */
  const { overtimeRequests, fetchMyOvertimeRequests } = useOvertimeStore();

  /* ================= DROPDOWNS – NEW ================= */
  const {
    options: dropdownOptions,
    loading: dropdownLoading,
    fetchBranches,
    fetchShifts,
    fetchDepartments,
    fetchDesignations,
    fetchRoles,
  } = useDropdownOptions();

  /* ================= DRAWER STATE ================= */
  const [drawerOpen, setDrawerOpen] = useState(false);

  /* ================= FILTER STATE ================= */
  const [filters, setFilters] = useState({
    branch_id: "",
    shift_id: "",
    department_id: "",
    designation_id: "",
    role: "",
    status: "",
    from: "",
    to: "",
  });

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm);

  /* ================= PAGINATION ================= */
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    last_page: 1,
    total: 0,
  });

  /* ================= LOADING ================= */
  const [pageLoading, setPageLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);

  /* ================= CALENDAR ================= */
  const [fromAnchor, setFromAnchor] = useState(null);
  const [toAnchor, setToAnchor] = useState(null);

  const lastQueryRef = useRef("");

  /* ================= FETCH ================= */
  const fetchOvertime = async (initial = false) => {
    const params = cleanParams({
      ...filters,
      q: debouncedSearch,
      page: pagination.current_page,
      per_page: pagination.per_page,
    });

    const queryKey = JSON.stringify(params);
    if (!initial && lastQueryRef.current === queryKey) return;
    lastQueryRef.current = queryKey;

    try {
      initial ? setPageLoading(true) : setTableLoading(true);

      const res = await fetchMyOvertimeRequests(params);

      const meta = res?.meta || res;

      if (meta && meta.current_page) {
        setPagination((p) => {
          if (
            p.current_page === meta.current_page &&
            p.last_page === meta.last_page &&
            p.total === meta.total &&
            p.per_page === meta.per_page
          ) {
            return p;
          }
          return {
            ...p,
            current_page: meta.current_page,
            last_page: meta.last_page,
            total: meta.total,
            per_page: meta.per_page,
          };
        });
      }
    } finally {
      setPageLoading(false);
      setTableLoading(false);
    }
  };

  /* ================= INITIAL LOAD ================= */
  useEffect(() => {
    if (canViewPage) fetchOvertime(true);
  }, [canViewPage]);

  /* ================= FILTER / SEARCH / PAGE ================= */
  useEffect(() => {
    if (canViewPage) fetchOvertime(false);
  }, [filters, debouncedSearch, pagination.current_page]);

  /* ================= FILTER HANDLER ================= */
  const handleFilterChange = (key, value) => {
    if (key === "__open_from__") {
      setFromAnchor(value);
      return;
    }
    if (key === "__open_to__") {
      setToAnchor(value);
      return;
    }

    setFilters((prev) => (prev[key] === value ? prev : { ...prev, [key]: value }));
    setPagination((p) => ({ ...p, current_page: 1 }));
  };

  /* ================= HELPERS ================= */
  const formatDate = (d) => (d ? dayjs(d).format("DD-MMM-YYYY") : "N/A");

  const formatMinutes = (m) => {
    if (!m) return "0h";
    const h = Math.floor(m / 60);
    const min = m % 60;
    return min ? `${h}h ${min}m` : `${h}h`;
  };

  /* ================= TABLE COLUMNS ================= */
  const columns = useMemo(
    () => [
      { Header: "Employee ID", accessor: "userId" },
      { Header: "Attendance Date", accessor: "attendanceDate" },
      { Header: "Reason", accessor: "reason" },
      { Header: "Expected Overtime (Min)", accessor: "expectedMinutes" },
      { Header: "Status", accessor: "status" },
      { Header: "Approved By", accessor: "approvedBy" },
      { Header: "Approved On", accessor: "approvedAt" },
      { Header: "Requested On", accessor: "requestedAt" },
    ],
    []
  );

  const safeRequests = useMemo(
    () => (Array.isArray(overtimeRequests) ? overtimeRequests.filter(Boolean) : []),
    [overtimeRequests]
  );

  const rows = useMemo(() => {
    return safeRequests.map((r) => ({
      userId: r?.user_id ?? "-",
      attendanceDate: r?.attendance_date ? dayjs(r.attendance_date).format("DD-MMM-YYYY") : "-",
      reason: r?.reason || "-",
      expectedMinutes: r?.expected_minutes ?? 0,
      status: (() => {
        const statusInfo = getLeaveStatusInfo(r.status);
        return (
          <SoftBox display="flex" alignItems="center" gap={0.5}>
            <Icon
              sx={{
                fontSize: "1rem",
                color: (theme) => theme.palette[statusInfo.color].main,
              }}
            >
              {statusInfo.icon}
            </Icon>
            <SoftTypography
              variant="button"
              fontWeight="medium"
              color={statusInfo.color}
              sx={{ textTransform: "capitalize", fontSize: "0.8rem" }}
            >
              {statusInfo.text}
            </SoftTypography>
          </SoftBox>
        );
      })(),
      approvedBy: r?.approver?.name || "-",
      approvedAt: r?.approved_at ? dayjs(r.approved_at).format("DD-MMM-YYYY HH:mm") : "-",
      requestedAt: r?.requested_at ? dayjs(r.requested_at).format("DD-MMM-YYYY HH:mm") : "-",
    }));
  }, [safeRequests]);

  /* ================= LOADING GUARD ================= */
  if (pageLoading) {
    return (
      <SoftBox p={3} textAlign="center">
        {/* <CircularProgress /> */}
        <FlagLoader />
      </SoftBox>
    );
  }

  /* ================= UI ================= */
  return (
    <Card>
      <SoftBox p={3}>
        {/* ✅ UPDATED FILTERS with new props */}
        <UserFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          dropdownOptions={dropdownOptions}
          loading={dropdownLoading}
          fetchBranches={fetchBranches}
          fetchShifts={fetchShifts}
          fetchDepartments={fetchDepartments}
          fetchDesignations={fetchDesignations}
          fetchRoles={fetchRoles}
          showHistoryFilters={true}
          pageContext="Attendance.MyOvertime"
        />

        <Grid container spacing={2} alignItems="center" justifyContent="space-between" mt={2} ml={1}>
          <Grid item>
            <SoftTypography variant="h6" color="text">
              My Overtime Requests ({pagination.total})
            </SoftTypography>
          </Grid>

          {canCreateOvertime && (
            <Grid item>
              <SoftButton
                color="info"
                variant="gradient"
                startIcon={<AddIcon />}
                onClick={() => setDrawerOpen(true)}
              >
                Add Overtime
              </SoftButton>
            </Grid>
          )}
        </Grid>

        {/* TABLE */}
        {tableLoading ? (
          <SoftBox py={4} textAlign="center">
            {/* <CircularProgress /> */}
            <FlagLoader />
          </SoftBox>
        ) : (
          <DataTable
            table={{ columns, rows }}
            entriesPerPage={false}
            canSearch={false}
            showTotalEntries
          />
        )}

        {/* PAGINATION */}
        {pagination.last_page > 1 && (
          <SoftBox mt={2} display="flex" justifyContent="center">
            <SoftPagination variant="gradient" color="info">
              {Array.from({ length: pagination.last_page }, (_, i) => (
                <SoftPagination
                  key={i}
                  item
                  active={pagination.current_page === i + 1}
                  onClick={() =>
                    setPagination((p) => ({
                      ...p,
                      current_page: i + 1,
                    }))
                  }
                >
                  {i + 1}
                </SoftPagination>
              ))}
            </SoftPagination>
          </SoftBox>
        )}
      </SoftBox>

      {/* FROM DATE */}
      <Popover open={Boolean(fromAnchor)} anchorEl={fromAnchor} onClose={() => setFromAnchor(null)}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateCalendar
            onChange={(d) => {
              handleFilterChange("from", d.format("YYYY-MM-DD"));
              setFromAnchor(null);
            }}
          />
        </LocalizationProvider>
      </Popover>

      {/* TO DATE */}
      <Popover open={Boolean(toAnchor)} anchorEl={toAnchor} onClose={() => setToAnchor(null)}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateCalendar
            onChange={(d) => {
              handleFilterChange("to", d.format("YYYY-MM-DD"));
              setToAnchor(null);
            }}
          />
        </LocalizationProvider>
      </Popover>

      {/* DRAWER */}
      {canCreateOvertime && (
        <OvertimeDrawer
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            fetchOvertime(true);
          }}
        />
      )}
    </Card>
  );
}

export default MyOvertimeRequest;